import { ApiTarget, HttpClient } from '@packages/core/http';
import type {
    CvtConcernDoc,
    FetchVinResponse,
    FetchVinResponseParams,
    PublicationsDto,
    RecentlyViewed,
    StisDocumentCount,
    StisQaSideNavCount,
} from '../types';

export class VinSessionService {
    constructor(private http: HttpClient) {}

    getVinData({
        fetchVinParams,
        ignoreCache,
        signal,
    }: {
        fetchVinParams: FetchVinResponseParams;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/vehicle-details' + this.http.queryString(fetchVinParams);

        return this.http.orchestrateRequest<FetchVinResponse>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getLatestVinData({
        fetchVinParams,
        ignoreCache,
        signal,
    }: {
        fetchVinParams: FetchVinResponseParams;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/vehicle-details/latest' + this.http.queryString(fetchVinParams);

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    openVinSession({ vin, ignoreCache, signal }: { vin: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/open-vin-session?vin=${vin}`;

        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    closeVinSession({ vin, ignoreCache, signal }: { vin: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/close-vin-session?vin=${vin}`;

        return this.http.orchestrateRequest<any>({
            method: 'PUT',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getRecentlyViewedVinData({
        ignoreCache,
        signal,
        accountId,
    }: {
        ignoreCache?: boolean;
        signal?: AbortSignal;
        accountId: string | any;
    }) {
        const url = `/recent-vin-sessions`;

        return this.http.orchestrateRequest<RecentlyViewed[]>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getActiveVinSessions({
        ignoreCache,
        signal,
        accountId,
    }: {
        ignoreCache?: boolean;
        signal?: AbortSignal;
        accountId: string | any;
    }) {
        const url = `/active-vin-sessions`;

        return this.http.orchestrateRequest<RecentlyViewed[]>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getAllPublications({ uri, ignoreCache, signal }: { uri: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/docs/all-docs' + (uri.length > 0 ? (uri.startsWith('?') ? uri : `?${uri}`) : '');
        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getPublications({
        vin,
        body,
        uri,
        ignoreCache,
        signal,
    }: {
        vin: string;
        body: { carlineId: string; modelYear: number; docStatus: string; searchType: string };
        uri: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/docs/search-docs' + (uri.length > 0 ? (uri.startsWith('?') ? uri : `?${uri}`) : '');

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'PUT',
            body,
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getPublicationsCount({ vin, ignoreCache, signal }: { vin: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/docs/count-by-vin?vin=${vin}`;

        return this.http.orchestrateRequest<StisDocumentCount>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getQaSideNavCount({ vin, ignoreCache, signal }: { vin: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/dashboard/side-nav-counts?vin=${vin}`;

        return this.http.orchestrateRequest<StisQaSideNavCount>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    getPublicationsByQuery({
        uri,
        body,
        vin,
        publicFlag,
        ignoreCache = true,
        signal,
    }: {
        uri: string;
        body: any;
        vin: string;
        publicFlag: boolean;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = `/${publicFlag ? 'public' : 'docs'}/search-docs` + (uri.length > 0 ? `${uri}` : '');

        return this.http.orchestrateRequest<PublicationsDto>({
            method: 'PUT',
            url,
            body: body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getPublicPublicationsByQuery({
        uri,
        body,
        ignoreCache = true,
        signal,
    }: {
        uri: string;
        body: any;
        vin: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/public/search-docs' + (uri.length > 0 ? `${uri}` : '');

        const requestOptions: any = {
            method: 'PUT',
            url,
            body,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        };

        return this.http.orchestrateRequest<PublicationsDto>(requestOptions);
    }

    postFavoriteDocument({
        queryParameters,
        ignoreCache,
        signal,
    }: {
        queryParameters: { docId: string; isFavorite: boolean };
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/docs/save-favorite${this.http.queryString(queryParameters)}`,
            apiTarget: ApiTarget.VinService,
            ignoreCache,
            signal,
        });
    }

    getFilter({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/docs/filter';

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getPublicFilter({ ignoreCache, signal }: { ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = '/public/filter';

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }

    getDTCC({ query, ignoreCache, signal }: { query: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/dtc-codes?query=${query}`;

        return this.http.orchestrateRequest<{
            dtcCodes: { dtcCodeId: string; dtcCode: string; description: string }[];
        }>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    getVinSessionCDNCookie(carLineId: string, modelYear: number) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/docs/vin-session-cdn-cookie-url?carLineId=${carLineId}&modelYear=${modelYear}`,
            apiTarget: ApiTarget.VinService,
        });
    }

    getVinBinDetails({ vin, ignoreCache, signal }: { vin: string; ignoreCache?: boolean; signal?: AbortSignal }) {
        const url = `/vehicle-details?vin=${vin}`;

        return this.http.orchestrateRequest<
            {
                vinRecord: {
                    fullVin: string | null;
                    vin: string;
                    model: string;
                    modelYear: number;
                    modelNumber: string;
                    colorCategory: string;
                    colorDescription: string;
                    warrantyStartDT: string;
                    productionDate: string;
                    engineId: string;
                    engineSize: number;
                    engineSizeL: number;
                    vehicleTransmissionNumber: string;
                    transmissionType: string;
                    transmission: string;
                    emissionSpec: string | null;
                    turboIndicator: string;
                    trimDescription: string;
                    driveTrain: string;
                    vinStatus: string;
                    fuelType: string;
                    optionCode: string;
                };
                evBattery: {
                    bin: string;
                    eventDate: string;
                };
                vehicleImageUrl: string;
                carlineId: string;
            }[]
        >({
            method: 'GET',
            url,
            ignoreCache,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }
    /**
     * Fetches the presigned URLs for TSB (Technical Service Bulletins) documents related to CVT concerns.
     */
    getCvtConcernDocs({ signal }: { signal?: AbortSignal }) {
        const url = `/docs/cvt-concern-docs`;

        return this.http.orchestrateRequest<CvtConcernDoc[]>({
            method: 'GET',
            url,
            ignoreCache: true,
            signal,
            apiTarget: ApiTarget.VinService,
        });
    }
}
